import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  Clock,
  Truck,
  Warning,
  Timer,
  Info,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";
import {
  ActionItems,
  RESPONSE_TIME_SLA_HOURS,
} from "../types/statistics.types";

interface ActionItemsTableProps {
  data: ActionItems | null;
  loading: boolean;
  onTicketClick?: (ticketId: number) => void;
}

// Translate source to Dutch
const translateSource = (source: string): string => {
  const translations: Record<string, string> = {
    TENANT: "Huurder",
    Huurder: "Huurder",
    INTERNAL: "Intern",
    Intern: "Intern",
    OWNER: "Eigenaar",
    SUPPLIER: "Leverancier",
    OTHER: "Overig",
    Buur: "Buur",
    Onbekend: "Onbekend",
  };
  return translations[source] || source || "Onbekend";
};

// Data source info component
function DataSourceInfo({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className="ml-1 inline-flex">
            <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-[350px] text-xs leading-relaxed"
        >
          <p className="font-medium mb-1">Data bron:</p>
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Collapsible section component
function CollapsibleSection({
  title,
  icon,
  iconColor,
  count,
  badgeClass,
  description,
  dataSource,
  children,
  defaultOpen = true,
}: {
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  count: number;
  badgeClass: string;
  description: string;
  dataSource?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between text-left"
        >
          <CardTitle className="flex items-center gap-2 text-lg">
            {isOpen ? (
              <CaretDown className="h-4 w-4" />
            ) : (
              <CaretRight className="h-4 w-4" />
            )}
            <span className={iconColor}>{icon}</span>
            {title}
            {dataSource && <DataSourceInfo text={dataSource} />}
          </CardTitle>
          <Badge variant="outline" className={badgeClass}>
            {count} tickets
          </Badge>
        </button>
        <p className="text-muted-foreground text-xs pl-6">{description}</p>
      </CardHeader>
      {isOpen && <CardContent>{children}</CardContent>}
    </Card>
  );
}

export function ActionItemsTable({
  data,
  loading,
  onTicketClick,
}: ActionItemsTableProps) {
  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="bg-white">
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[200px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const handleRowClick = (ticketId: number) => {
    if (onTicketClick) {
      onTicketClick(ticketId);
    } else {
      window.open(`/helpdesk/${ticketId}`, "_blank");
    }
  };

  const formatHoursToDisplay = (hours: number) => {
    if (hours < 24) {
      return `${hours}u`;
    }
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) {
      return `${days}d`;
    }
    return `${days}d ${remainingHours}u`;
  };

  return (
    <div className="space-y-6">
      {/* Response Time SLA Section - NEW */}
      <CollapsibleSection
        title="Reactietijd SLA"
        icon={<Timer className="h-5 w-5" weight="duotone" />}
        iconColor="text-orange-500"
        count={data?.counts.responseTimeAtRisk ?? 0}
        badgeClass="bg-orange-50 text-orange-700"
        description={`Tickets zonder eerste reactie binnen ${RESPONSE_TIME_SLA_HOURS} uur (3 dagen)`}
        dataSource={data?.dataSources?.responseTimeAtRisk}
        defaultOpen={(data?.counts.responseTimeAtRisk ?? 0) > 0}
      >
        <div className="bg-muted/30 mb-4 flex flex-wrap items-center gap-4 rounded-md px-3 py-2 text-xs">
          <span className="text-muted-foreground font-medium">
            Definitie:
          </span>
          <span>
            Van <Badge variant="outline" className="mx-1 h-5 text-[10px]">createdAt</Badge>
            tot eerste fase-overgang uit "Unknown" of "Nieuwe tickets"
          </span>
          <span className="text-muted-foreground">|</span>
          <span>
            Target: <strong>{RESPONSE_TIME_SLA_HOURS} uur</strong> (3 dagen)
          </span>
        </div>

        {(data?.responseTimeAtRisk?.length ?? 0) === 0 ? (
          <div className="text-muted-foreground py-8 text-center">
            <Timer className="mx-auto mb-2 h-12 w-12 opacity-50" />
            <p>Alle tickets hebben tijdig een reactie ontvangen</p>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Titel</TableHead>
                  <TableHead>Gebouw</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Huidige fase</TableHead>
                  <TableHead>Bron</TableHead>
                  <TableHead className="text-right">Wachttijd</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.responseTimeAtRisk?.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleRowClick(ticket.id)}
                  >
                    <TableCell className="font-medium">H{ticket.id}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {ticket.title}
                    </TableCell>
                    <TableCell>{ticket.building}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{ticket.label}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {ticket.currentPhase === "Unknown"
                        ? "Onbekend"
                        : "Nieuw (onbeantwoord)"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {translateSource(ticket.source)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant={
                          ticket.hoursWithoutResponse >= RESPONSE_TIME_SLA_HOURS
                            ? "destructive"
                            : "outline"
                        }
                        className={
                          ticket.hoursWithoutResponse >= RESPONSE_TIME_SLA_HOURS
                            ? "bg-red-100 text-red-800"
                            : "bg-orange-50 text-orange-700"
                        }
                      >
                        {formatHoursToDisplay(ticket.hoursWithoutResponse)}
                        {ticket.hoursWithoutResponse >= RESPONSE_TIME_SLA_HOURS
                          ? " (over SLA)"
                          : ""}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleSection>

      {/* Doorlooptijd SLA Section */}
      <CollapsibleSection
        title="Doorlooptijd SLA Risico"
        icon={<Warning className="h-5 w-5" weight="duotone" />}
        iconColor="text-red-500"
        count={data?.counts.slaAtRisk ?? 0}
        badgeClass="bg-red-50 text-red-700"
        description="Tickets die de doorlooptijd SLA naderen of overschreden hebben"
        dataSource={data?.dataSources?.slaAtRisk}
        defaultOpen={(data?.counts.slaAtRisk ?? 0) > 0}
      >
        <div className="bg-muted/30 mb-4 rounded-md px-3 py-2 text-xs space-y-2">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-muted-foreground font-medium">
              Definitie:
            </span>
            <span>
              Van <Badge variant="outline" className="mx-1 h-5 text-[10px]">createdAt</Badge>
              tot <Badge variant="outline" className="mx-1 h-5 text-[10px]">completedAt</Badge>
              (fase "Voltooid")
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-muted-foreground font-medium">Targets:</span>
            <span className="flex items-center gap-1.5">
              <Badge
                variant="destructive"
                className="h-5 px-1.5 text-[10px] font-medium"
              >
                Hoog
              </Badge>
              <span>5 dagen</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Badge
                variant="secondary"
                className="h-5 px-1.5 text-[10px] font-medium"
              >
                Gemiddeld
              </Badge>
              <span>10 dagen</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Badge
                variant="secondary"
                className="h-5 px-1.5 text-[10px] font-medium"
              >
                Laag
              </Badge>
              <span>20 dagen</span>
            </span>
          </div>
        </div>

        {(data?.slaAtRisk?.length ?? 0) === 0 ? (
          <div className="text-muted-foreground py-8 text-center">
            <Warning className="mx-auto mb-2 h-12 w-12 opacity-50" />
            <p>Geen tickets met doorlooptijd SLA risico</p>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Titel</TableHead>
                  <TableHead>Prioriteit</TableHead>
                  <TableHead>SLA Target</TableHead>
                  <TableHead>Gebouw</TableHead>
                  <TableHead>Bron</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.slaAtRisk?.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleRowClick(ticket.id)}
                  >
                    <TableCell className="font-medium">H{ticket.id}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {ticket.title}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          ticket.priority === "Hoog"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {ticket.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {ticket.slaTarget} dagen
                    </TableCell>
                    <TableCell>{ticket.building}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {translateSource(ticket.source)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant={
                          ticket.daysRemaining <= 0
                            ? "destructive"
                            : ticket.daysRemaining <= 2
                              ? "outline"
                              : "secondary"
                        }
                        className={
                          ticket.daysRemaining <= 0
                            ? "bg-red-100 text-red-800"
                            : ""
                        }
                      >
                        {ticket.daysRemaining <= 0
                          ? `${Math.abs(ticket.daysRemaining)}d over SLA`
                          : `${ticket.daysRemaining}d resterend`}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleSection>

      {/* Stuck Tickets Section */}
      <CollapsibleSection
        title="Vastgelopen tickets"
        icon={<Clock className="h-5 w-5" weight="duotone" />}
        iconColor="text-yellow-500"
        count={data?.counts.stuck ?? 0}
        badgeClass="bg-yellow-50 text-yellow-700"
        description="Tickets zonder fase-wijziging gedurende meer dan 5 dagen"
        dataSource={data?.dataSources?.stuckTickets}
        defaultOpen={(data?.counts.stuck ?? 0) > 0}
      >
        {(data?.stuckTickets?.length ?? 0) === 0 ? (
          <div className="text-muted-foreground py-8 text-center">
            <Clock className="mx-auto mb-2 h-12 w-12 opacity-50" />
            <p>Geen vastgelopen tickets</p>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Titel</TableHead>
                  <TableHead>Gebouw</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Bron</TableHead>
                  <TableHead className="text-right">Dagen inactief</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.stuckTickets?.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleRowClick(ticket.id)}
                  >
                    <TableCell className="font-medium">H{ticket.id}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {ticket.title}
                    </TableCell>
                    <TableCell>{ticket.building}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{ticket.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {translateSource(ticket.source)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant={
                          ticket.daysSinceActivity > 10
                            ? "destructive"
                            : "outline"
                        }
                      >
                        {ticket.daysSinceActivity} dagen
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleSection>

      {/* Pending Supplier Section */}
      <CollapsibleSection
        title="Wachtend op leverancier"
        icon={<Truck className="h-5 w-5" weight="duotone" />}
        iconColor="text-blue-500"
        count={data?.counts.pendingSupplier ?? 0}
        badgeClass="bg-blue-50 text-blue-700"
        description="Tickets in leverancier-fase voor meer dan 3 dagen"
        dataSource={data?.dataSources?.pendingSupplier}
        defaultOpen={(data?.counts.pendingSupplier ?? 0) > 0}
      >
        {(data?.pendingSupplier?.length ?? 0) === 0 ? (
          <div className="text-muted-foreground py-8 text-center">
            <Truck className="mx-auto mb-2 h-12 w-12 opacity-50" />
            <p>Geen tickets wachtend op leverancier</p>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Titel</TableHead>
                  <TableHead>Leverancier</TableHead>
                  <TableHead>Gebouw</TableHead>
                  <TableHead>Bron</TableHead>
                  <TableHead className="text-right">Dagen wachtend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.pendingSupplier?.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleRowClick(ticket.id)}
                  >
                    <TableCell className="font-medium">H{ticket.id}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {ticket.title}
                    </TableCell>
                    <TableCell>{ticket.supplier}</TableCell>
                    <TableCell>{ticket.building}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {translateSource(ticket.source)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant={
                          ticket.daysInStatus > 5 ? "destructive" : "outline"
                        }
                      >
                        {ticket.daysInStatus} dagen
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
}
